import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Plus, 
  Search, 
  Download, 
  Package, 
  ScanLine, 
  Layers, 
  Tag, 
  AlertTriangle, 
  ArrowUpRight,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import Papa from 'papaparse';
import JsBarcode from 'jsbarcode';
import { AuthContext } from '../contexts/AuthContext';

export default function Inventory() {
  const [activeTab, setActiveTab] = useState('stock');
  const [products, setProducts] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdjModalOpen, setIsAdjModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [adjData, setAdjData] = useState({ quantity: '', reason: '' });
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newProd, setNewProd] = useState({ name: '', sku: '', categoryId: '', price: '', cost: '', stock: '', lowStockThreshold: '10' });
  const [filterLowStock, setFilterLowStock] = useState(false);
  const [isBarcodeModalOpen, setIsBarcodeModalOpen] = useState(false);
  const [barcodeProduct, setBarcodeProduct] = useState(null);
  const [importing, setImporting] = useState(false);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  const { user } = useContext(AuthContext);

  const loadData = async () => {
    setLoading(true);
    try {
      const [prodRes, logsRes, catRes] = await Promise.all([
        api.get('/products'),
        api.get('/inventory/logs'),
        api.get('/products/categories')
      ]);
      setProducts(prodRes.data);
      setLogs(logsRes.data);
      setCategories(catRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    try {
      await api.post('/products', {
        ...newProd,
        price: parseFloat(newProd.price),
        cost: parseFloat(newProd.cost),
        stock: parseInt(newProd.stock),
        lowStockThreshold: parseInt(newProd.lowStockThreshold),
        categoryId: newProd.categoryId ? parseInt(newProd.categoryId) : null
      });
      setIsRegisterModalOpen(false);
      setNewProd({ name: '', sku: '', categoryId: '', price: '', cost: '', stock: '', lowStockThreshold: '10' });
      loadData();
    } catch (err) {
      alert('Registration failed');
    }
  };

  const handleAdjust = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/inventory/adjust`, {
        productId: selectedProduct.id,
        quantity: parseInt(adjData.quantity),
        reason: adjData.reason
      });
      setIsAdjModalOpen(false);
      setAdjData({ quantity: '', reason: '' });
      loadData();
    } catch (err) {
      alert('Adjustment failed');
    }
  };

  const handleExport = () => {
    const csvData = products.map(p => ({
      Name: p.name, SKU: p.sku, Category: p.category?.name || '',
      Price: p.price, Cost: p.cost, Stock: p.stock, 'Low Stock Alert': p.lowStockThreshold
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `inventory_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImporting(true);
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (results) => {
        try {
          const formatted = results.data.map(row => ({
            name: row.Name || row.name, sku: row.SKU || row.sku,
            price: row.Price || row.price, cost: row.Cost || row.cost || 0,
            stock: row.Stock || row.stock || 0, lowStockThreshold: row['Low Stock Alert'] || row.lowStockThreshold || 5
          }));
          await api.post('/products/bulk', { products: formatted });
          alert(`Successfully imported ${formatted.length} products!`);
          loadData();
        } catch (err) {
          alert('Import failed');
        } finally {
          setImporting(false);
          e.target.value = null;
        }
      }
    });
  };

  const openBarcodeModal = (p) => {
    setBarcodeProduct(p);
    setIsBarcodeModalOpen(true);
    setTimeout(() => {
      if (document.getElementById('barcode-svg')) {
        JsBarcode('#barcode-svg', p.sku, { format: 'CODE128', width: 2, height: 60, displayValue: true, fontSize: 16 });
      }
    }, 100);
  };

  const printBarcode = () => {
    const content = document.getElementById('barcode-print-area').innerHTML;
    const win = window.open('', '_blank');
    win.document.write(`<html><head><title>Print Barcode</title><style>body { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100vh; font-family: sans-serif; } .label { text-align: center; border: 1px solid #eee; padding: 20px; border-radius: 10px; } h2 { margin-bottom: 5px; } p { margin-top: 5px; font-weight: bold; font-size: 20px; }</style></head><body><div class="label"><h2>${barcodeProduct.name}</h2>${content}<p>$${barcodeProduct.price.toFixed(2)}</p></div><script>window.onload = () => { window.print(); window.close(); }</script></body></html>`);
    win.document.close();
  };

  const lowStockCount = products.filter(p => p.stock <= p.lowStockThreshold).length;
  const totalValue = products.reduce((acc, p) => acc + (p.price * p.stock), 0);

  const filteredItems = (activeTab === 'stock' ? products : logs).filter(item => {
    const name = activeTab === 'stock' ? item.name : item.product?.name;
    const matchesSearch = name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesLowStock = activeTab === 'stock' && filterLowStock ? item.stock <= item.lowStockThreshold : true;
    return matchesSearch && matchesLowStock;
  });

  // Pagination
  const totalItems = filteredItems.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredItems.slice(indexOfFirstItem, indexOfLastItem);

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Master Inventory</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Manage stock levels and track warehouse movements.</p>
        </div>
        <div className="flex gap-3">
          <label className={`flex items-center gap-2 px-4 py-2 rounded-xl cursor-pointer transition-all shadow-md font-bold text-sm ${importing ? 'bg-theme-main text-theme-muted' : 'bg-theme-card border border-theme text-theme-main hover:border-brand'}`}>
            <ArrowUpRight size={18} /> {importing ? 'Importing...' : 'Bulk Import'}
            <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
          </label>
          <button onClick={() => setIsRegisterModalOpen(true)} className="flex items-center gap-2 bg-brand text-white px-4 py-2 rounded-xl hover:bg-brand/90 transition-all shadow-md font-bold text-sm">
            <Plus size={18} /> Register Item
          </button>
          <div className="flex items-center gap-4 bg-theme-card px-4 py-2 rounded-xl shadow-soft border border-theme">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold text-theme-muted uppercase tracking-wider">Value:</span>
              <span className="text-sm font-black text-theme-main">${totalValue.toLocaleString()}</span>
            </div>
            <div className="w-px h-4 border-l border-theme"></div>
            <div className="flex items-center gap-2">
              <AlertTriangle size={14} className={lowStockCount > 0 ? "text-amber-500" : "text-theme-muted"} />
              <span className={`text-sm font-black ${lowStockCount > 0 ? "text-amber-600" : "text-theme-main"}`}>{lowStockCount}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex border-b border-theme">
        <button onClick={() => { setActiveTab('stock'); setCurrentPage(1); }} className={`px-6 py-3 text-sm font-black transition-all border-b-2 ${activeTab === 'stock' ? 'border-brand text-brand' : 'border-transparent text-theme-muted hover:text-theme-main'}`}>Current Stock</button>
        <button onClick={() => { setActiveTab('trail'); setCurrentPage(1); }} className={`px-6 py-3 text-sm font-black transition-all border-b-2 ${activeTab === 'trail' ? 'border-brand text-brand' : 'border-transparent text-theme-muted hover:text-theme-main'}`}>Audit Trail</button>
      </div>

      <div className="bg-theme-card rounded-[2rem] shadow-soft border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme bg-theme-main/10 flex flex-col sm:flex-row gap-4 items-center">
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={18} />
            <input type="text" placeholder={`Search...`} className="w-full pl-10 pr-4 py-2.5 bg-theme-main border border-theme rounded-xl text-sm outline-none focus:ring-2 focus:ring-brand transition-all text-theme-main font-bold" value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} />
          </div>
          <div className="flex gap-2">
            <button onClick={() => { setFilterLowStock(!filterLowStock); setCurrentPage(1); }} className={`flex items-center gap-2 border px-4 py-2 rounded-xl text-sm font-bold transition-all shadow-sm ${filterLowStock ? 'bg-rose-500 text-white border-rose-600' : 'bg-theme-card border-theme text-theme-muted hover:border-brand'}`}><AlertTriangle size={16} /> {filterLowStock ? 'Showing Low' : 'Filter Low'}</button>
            <button onClick={handleExport} className="flex items-center gap-2 bg-theme-card border border-theme text-theme-muted px-4 py-2 rounded-xl text-sm font-bold hover:border-brand shadow-sm"><Download size={16} /> Export</button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-bold border-b border-theme">
                {activeTab === 'stock' ? (
                  <>
                    <th className="px-6 py-4">Product Name</th>
                    <th className="px-6 py-4">SKU</th>
                    <th className="px-6 py-4">Category</th>
                    <th className="px-6 py-4">Current Stock</th>
                    <th className="px-8 py-4 text-right">Actions</th>
                  </>
                ) : (
                  <>
                    <th className="px-6 py-4">Date</th>
                    <th className="px-6 py-4">Product</th>
                    <th className="px-6 py-4">Type</th>
                    <th className="px-6 py-4">Qty</th>
                    <th className="px-6 py-4">Reason</th>
                    <th className="px-6 py-4">User</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentItems.map(item => (
                <tr key={item.id} className="hover:bg-theme-main/10 transition-colors">
                  {activeTab === 'stock' ? (
                    <>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg bg-brand/10 flex items-center justify-center text-brand"><Package size={16} /></div>
                          <div className="text-sm font-bold text-theme-main">{item.name}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-xs font-mono text-theme-muted">{item.sku}</td>
                      <td className="px-6 py-4 text-sm text-theme-muted">{item.category?.name || 'Uncategorized'}</td>
                      <td className="px-6 py-4"><div className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-sm font-black ${item.stock <= item.lowStockThreshold ? 'bg-rose-500/10 text-rose-500' : 'bg-emerald-500/10 text-emerald-500'}`}>{item.stock} {item.stock <= item.lowStockThreshold && <AlertTriangle size={12} />}</div></td>
                      <td className="px-8 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <button onClick={() => openBarcodeModal(item)} className="p-1.5 text-theme-muted hover:text-brand hover:bg-brand/10 rounded-lg transition-all"><ScanLine size={18} /></button>
                          <button onClick={() => { setSelectedProduct(item); setIsAdjModalOpen(true); }} className="bg-brand hover:bg-brand/90 text-white px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm flex items-center gap-1"><Layers size={14} /> Adjust</button>
                        </div>
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="px-6 py-4 text-theme-muted text-xs">{new Date(item.createdAt).toLocaleString()}</td>
                      <td className="px-6 py-4 font-bold text-theme-main text-xs">{item.product?.name}</td>
                      <td className="px-6 py-4"><span className={`px-2 py-0.5 rounded-full font-bold text-[10px] ${item.type === 'SALE' ? 'bg-blue-500/10 text-blue-500' : 'bg-amber-500/10 text-amber-500'}`}>{item.type}</span></td>
                      <td className={`px-6 py-4 font-black text-xs ${item.quantity > 0 ? 'text-emerald-500' : 'text-rose-500'}`}>{item.quantity > 0 ? `+${item.quantity}` : item.quantity}</td>
                      <td className="px-6 py-4 text-theme-muted italic text-[10px]">{item.reason}</td>
                      <td className="px-6 py-4 text-theme-main font-bold text-xs">{item.user?.name || 'System'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> items
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronLeft size={20} /></button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronRight size={20} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Modals remain largely the same */}
      {isAdjModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-theme">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-lg font-black text-theme-main uppercase tracking-tight">Adjust Stock: {selectedProduct.name}</h3>
              <button onClick={() => setIsAdjModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors"><X size={24} /></button>
            </div>
            <form onSubmit={handleAdjust} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Adjustment Quantity</label>
                <input required type="number" placeholder="Use - for removal (e.g. -10)" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={adjData.quantity} onChange={e => setAdjData({...adjData, quantity: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Reason for Adjustment</label>
                <textarea required className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main text-sm font-bold" rows="3" placeholder="e.g. Damage" value={adjData.reason} onChange={e => setAdjData({...adjData, reason: e.target.value})}></textarea>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setIsAdjModalOpen(false)} className="flex-1 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-black uppercase text-[10px] tracking-widest">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-brand text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-brand/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isRegisterModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-theme">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight">Register Product</h3>
              <button onClick={() => setIsRegisterModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors"><X size={24} /></button>
            </div>
            <form onSubmit={handleRegister} className="p-6">
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Product Name</label>
                  <input required type="text" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={newProd.name} onChange={e => setNewProd({...newProd, name: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">SKU / Barcode</label>
                  <input required type="text" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={newProd.sku} onChange={e => setNewProd({...newProd, sku: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Category</label>
                  <select required className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none text-theme-main font-bold" value={newProd.categoryId} onChange={e => setNewProd({...newProd, categoryId: e.target.value})}>
                    <option value="">Select Category</option>
                    {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Price ($)</label>
                  <input required type="number" step="0.01" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none font-black text-brand" value={newProd.price} onChange={e => setNewProd({...newProd, price: e.target.value})} />
                </div>
                <div>
                  <label className="block text-xs font-bold text-theme-muted uppercase tracking-widest mb-1">Cost ($)</label>
                  <input required type="number" step="0.01" className="w-full px-4 py-2.5 bg-theme-main border border-theme rounded-xl focus:ring-2 focus:ring-brand outline-none font-black text-theme-muted" value={newProd.cost} onChange={e => setNewProd({...newProd, cost: e.target.value})} />
                </div>
              </div>
              <div className="flex gap-3 pt-4 border-t border-theme">
                <button type="button" onClick={() => setIsRegisterModalOpen(false)} className="flex-1 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-black uppercase text-[10px] tracking-widest">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-brand text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-brand/20">Register</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isBarcodeModalOpen && (
        <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden border border-theme">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center"><h3 className="text-lg font-black text-theme-main uppercase tracking-tight">Label</h3><button onClick={() => setIsBarcodeModalOpen(false)} className="text-theme-muted hover:text-theme-main p-1.5 rounded-lg transition-colors"><X size={24} /></button></div>
            <div className="p-8 flex flex-col items-center">
              <div className="text-center mb-6"><h4 className="text-xl font-black text-theme-main uppercase tracking-tight mb-1">{barcodeProduct.name}</h4><p className="text-brand font-black text-2xl tracking-tighter">${barcodeProduct.price.toFixed(2)}</p></div>
              <div id="barcode-print-area" className="bg-white p-6 rounded-2xl border border-theme flex items-center justify-center"><svg id="barcode-svg"></svg></div>
              <div className="w-full mt-8 flex gap-3">
                <button onClick={() => setIsBarcodeModalOpen(false)} className="flex-1 py-3 bg-theme-main/20 text-theme-muted rounded-xl font-bold uppercase text-[10px] tracking-widest">Close</button>
                <button onClick={printBarcode} className="flex-1 py-3 bg-brand text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-lg shadow-brand/20">Print</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}